import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Hash, Users, MessageCircle, Music, Edit3, Trash2, Check, XCircle, Smile } from 'lucide-react';
import EmotePicker from './EmotePicker';
import EmoteInput from './EmoteInput';
import { renderEmotesInText } from '../utils/emoteRenderer';
import ReactionPicker from './ReactionPicker';
import Reactions from './Reactions';
import PresenceDot from './PresenceDot';
import ChatInviteModal from './ChatInviteModal';
import WhisperIcon from './WhisperIcon';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

console.log('BACKEND_URL in chat:', BACKEND_URL);

const NewCommunityChat = ({ user, isOpen, onClose, onWhisperClick, navigationState }) => {
  // State management
  const [currentRoom, setCurrentRoom] = useState(null);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [showEmotePicker, setShowEmotePicker] = useState(false);
  const [globalEmotes, setGlobalEmotes] = useState([]);
  const [customEmotes, setCustomEmotes] = useState([]);
  const [showReactionPicker, setShowReactionPicker] = useState(null); // message ID
  const [userPresence, setUserPresence] = useState({}); // {user_id: {online: bool}}
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [roomPresence, setRoomPresence] = useState({});
  
  // Mobile state management
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight);
  const [mobileView, setMobileView] = useState('rooms'); // 'rooms' or 'chat'
  
  // Refs
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const lastMessageTimeRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);

  // Mobile/orientation detection
  useEffect(() => {
    const checkMobileAndOrientation = () => {
      const mobile = window.innerWidth < 768;
      const landscape = window.innerWidth > window.innerHeight;
      setIsMobile(mobile);
      setIsLandscape(landscape);
    };
    
    checkMobileAndOrientation();
    window.addEventListener('resize', checkMobileAndOrientation);
    window.addEventListener('orientationchange', checkMobileAndOrientation);
    
    return () => {
      window.removeEventListener('resize', checkMobileAndOrientation);
      window.removeEventListener('orientationchange', checkMobileAndOrientation);
    };
  }, []);

  // When a room is selected on mobile portrait, switch to chat view
  const handleRoomSelect = (room) => {
    joinRoom(room);
    if (isMobile && !isLandscape) {
      setMobileView('chat');
    }
  };

  // Back to rooms list (mobile portrait)
  const handleBackToRooms = () => {
    setMobileView('rooms');
  };

  // Fetch available rooms
  const loadRooms = async () => {
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/chat/rooms-new`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const rooms = await response.json();
        setAvailableRooms(rooms);
        setIsConnected(true);
        setConnectionError(null);
      } else {
        throw new Error('Failed to load rooms');
      }
    } catch (error) {
      console.error('Failed to load rooms:', error);
      setConnectionError('Failed to connect to chat service');
      setIsConnected(false);
    }
  };

  // Fetch emotes
  const loadEmotes = async () => {
    try {
      // Fetch global emotes
      const globalResponse = await fetch(`${API}/emotes/global`);
      if (globalResponse.ok) {
        const global = await globalResponse.json();
        setGlobalEmotes(global);
      }
      
      // Fetch user's custom emotes (if logged in)
      const token = localStorage.getItem('beatseek_token');
      if (token) {
        const customResponse = await fetch(`${API}/emotes/my-custom`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (customResponse.ok) {
          const custom = await customResponse.json();
          setCustomEmotes(custom);
        }
      }
    } catch (error) {
      console.error('Failed to load emotes:', error);
    }
  };

  // Join a room
  const joinRoom = async (room) => {
    try {
      const token = localStorage.getItem('beatseek_token');
      
      // Leave current room first
      if (currentRoom) {
        await fetch(`${API}/chat/rooms/${currentRoom.id}/leave`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
      
      // Join new room
      const response = await fetch(`${API}/chat/rooms/${room.id}/join`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        setCurrentRoom(room);
        setMessages([]);
        lastMessageTimeRef.current = null;
        
        // Load initial messages
        await loadMessages(room.id);
        
        // Start polling for new messages
        startPolling(room.id);
      } else {
        // If join failed (e.g., guest user), still allow viewing the room
        console.warn('Join room failed, allowing guest viewing:', response.status);
        setCurrentRoom(room);
        setMessages([]);
        lastMessageTimeRef.current = null;
        
        // Load messages even for guests
        await loadMessages(room.id);
        
        // Start polling for guests too
        startPolling(room.id);
      }
    } catch (error) {
      console.error('Failed to join room:', error);
    }
  };

  // Load messages for a room
  const loadMessages = async (roomId, since = null) => {
    try {
      const token = localStorage.getItem('beatseek_token');
      
      // Always use debug endpoint (it works reliably)
      let url = `${API}/debug/messages/${roomId}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const newMessages = data.messages || [];
        
        if (since) {
          // For polling - filter by timestamp AND deduplicate
          if (newMessages.length > 0) {
            setMessages(prev => {
              // Get IDs and timestamps of existing messages
              const existingIds = new Set(prev.map(msg => msg.id));
              const lastTimestamp = lastMessageTimeRef.current;
              
              // Filter messages newer than our last timestamp AND not already existing
              const sinceDate = new Date(lastTimestamp);
              const uniqueNewMessages = newMessages.filter(msg => {
                const msgDate = new Date(msg.timestamp);
                return !existingIds.has(msg.id) && msgDate > sinceDate;
              });
              
              if (uniqueNewMessages.length > 0) {
                console.log(`Adding ${uniqueNewMessages.length} new messages since ${sinceDate.toLocaleTimeString()}`);
                // Update timestamp to latest message
                const latestMsg = uniqueNewMessages[uniqueNewMessages.length - 1];
                lastMessageTimeRef.current = latestMsg.timestamp;
                return [...prev, ...uniqueNewMessages];
              }
              return prev;
            });
          }
        } else {
          // Set initial messages (first load)
          setMessages(newMessages);
          if (newMessages.length > 0) {
            // Set timestamp to latest message
            const latestMsg = newMessages[newMessages.length - 1];
            lastMessageTimeRef.current = latestMsg.timestamp;
          }
        }
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  // Start polling for new messages
  const startPolling = (roomId) => {
    // Clear existing polling
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    
    // Poll every 5 seconds for new messages (less aggressive)
    pollIntervalRef.current = setInterval(() => {
      // Only poll if we have messages and room is still active
      if (lastMessageTimeRef.current && currentRoom && currentRoom.id === roomId) {
        loadMessages(roomId, lastMessageTimeRef.current);
      }
    }, 5000);
  };

  // Send message
  const sendMessage = async () => {
    if (!messageInput.trim() || !currentRoom) return;
    
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/chat/rooms/${currentRoom.id}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: messageInput.trim() })
      });
      
      if (response.ok) {
        const sentMessage = await response.json();
        setMessageInput('');
        
        // Check if message was flagged by AI moderation
        if (sentMessage.moderation_status === 'pending_moderation') {
          // Show alert to user
          alert('⏳ Your message has been sent to admin for approval\n\nOur AI detected potentially inappropriate content. An admin will review your message shortly.');
          console.log('🚨 Message flagged for moderation:', sentMessage.moderation_reason);
          // Don't add to chat - it will only appear after admin approval
        } else {
          // Add the approved message immediately to the chat
          setMessages(prev => {
            const existingIds = new Set(prev.map(msg => msg.id));
            if (!existingIds.has(sentMessage.id)) {
              // Update timestamp reference
              lastMessageTimeRef.current = sentMessage.timestamp;
              return [...prev, sentMessage];
            }
            return prev;
          });
        }
      } else {
        console.error('Failed to send message:', response.status);
        alert('Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message. Please check your connection.');
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Edit message function
  const editMessage = async (messageId, newText) => {
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/chat/messages/${messageId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: newText })
      });

      if (response.ok) {
        // Update message in local state
        setMessages(prev => prev.map(msg => 
          msg.id === messageId 
            ? { ...msg, message: newText, edited_at: new Date().toISOString() }
            : msg
        ));
        setEditingMessageId(null);
        setEditingText('');
      } else {
        console.error('Failed to edit message:', response.status);
      }
    } catch (error) {
      console.error('Error editing message:', error);
    }
  };

  // Delete message function
  const deleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) {
      return;
    }

    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/chat/messages/${messageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Remove message from local state
        setMessages(prev => prev.filter(msg => msg.id !== messageId));
      } else {
        console.error('Failed to delete message:', response.status);
      }
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  // Add reaction to message
  const addReaction = async (messageId, emoji) => {
    try {
      const token = localStorage.getItem('beatseek_token');
      
      const response = await fetch(`${API}/chat/messages/${messageId}/reactions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ emoji })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Reaction added:', data);
        // Update message reactions in local state immediately
        setMessages(prev => prev.map(msg => 
          msg.id === messageId ? { ...msg, reactions: data.reactions } : msg
        ));
      } else {
        console.error('Failed to add reaction:', response.status);
        const errorText = await response.text();
        console.error('Error details:', errorText);
      }
    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  };

  // Send heartbeat to update presence
  const sendHeartbeat = async () => {
    try {
      const token = localStorage.getItem('beatseek_token');
      if (!token) return;
      
      await fetch(`${API}/presence/heartbeat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      await fetch(`${API}/chat/room-presence`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ room_id: currentRoom?.id || null }),
      });
    } catch (error) {
      console.error('Heartbeat error:', error);
    }
  };

  // Who's in each room right now
  const loadRoomPresence = async () => {
    try {
      const r = await fetch(`${API}/chat/rooms-presence`);
      if (r.ok) {
        const d = await r.json();
        setRoomPresence(d.rooms || {});
      }
    } catch { /* presence is best-effort */ }
  };

  // Fetch presence for visible users
  const fetchPresence = async () => {
    try {
      // Get unique user IDs from current messages
      const userIds = [...new Set(messages.map(msg => msg.user_id))];
      if (userIds.length === 0) return;
      
      const response = await fetch(`${API}/presence/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userIds)
      });

      if (response.ok) {
        const presenceData = await response.json();
        setUserPresence(presenceData);
      }
    } catch (error) {
      console.error('Error fetching presence:', error);
    }
  };

  // Start editing a message
  const startEditing = (message) => {
    setEditingMessageId(message.id);
    setEditingText(message.message);
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingMessageId(null);
    setEditingText('');
  };

  // Save edited message
  const saveEdit = () => {
    if (editingText.trim()) {
      editMessage(editingMessageId, editingText.trim());
    }
  };

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Render message with edit/delete functionality
  const renderMessage = (message) => {
    const isOwnMessage = message.user_id === user?.id;
    const isEditing = editingMessageId === message.id;
    // Current user is always online, others check presence data
    const userOnline = isOwnMessage ? true : (userPresence[message.user_id]?.online || false);
    const isHighlighted = highlightedMessageId === message.id;
    
    return (
      <div 
        key={message.id} 
        className={`group mb-4 text-left transition-all duration-300 ${
          isHighlighted ? 'bg-yellow-500/20 rounded-lg p-2 border border-yellow-500/50 shadow-lg' : ''
        }`}
      >
        <div className="inline-block text-left max-w-full">
          <div className="flex items-center space-x-2 mb-1 justify-start">
            {/* Small avatar next to username with presence dot */}
            <div className="relative">
              <div className="w-5 h-5 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 overflow-hidden">
                {(() => {
                  // Special case for Soundstixx admin - use known avatar path  
                  if (isOwnMessage && (message.user_name === 'Soundstixx' || user?.name === 'Soundstixx')) {
                    const adminAvatarUrl = `${BACKEND_URL}/api/uploads/eb285394-2b4f-4be2-bf8f-c16e22c742d1_avatar_2635e320.png`;
                    return (
                      <img 
                        src={adminAvatarUrl} 
                        alt="Soundstixx" 
                        className="w-full h-full rounded-full object-cover"
                        onError={(e) => {
                          console.log('Soundstixx avatar failed to load:', adminAvatarUrl);
                          e.target.style.display = 'none';
                        }}
                        onLoad={() => console.log('Soundstixx avatar loaded successfully!')}
                      />
                    );
                  } else if (isOwnMessage && user?.profile_picture) {
                    const avatarUrl = user.profile_picture.startsWith('/uploads/') ? `${BACKEND_URL}/api${user.profile_picture}` : user.profile_picture.startsWith('/') ? `${BACKEND_URL}${user.profile_picture}` : user.profile_picture;
                    return (
                      <img 
                        src={avatarUrl} 
                        alt={user.name} 
                        className="w-full h-full rounded-full object-cover"
                      />
                    );
                  } else if (message.user_avatar) {
                    const avatarUrl = message.user_avatar.startsWith('/uploads/') ? `${BACKEND_URL}/api${message.user_avatar}` : message.user_avatar.startsWith('/') ? `${BACKEND_URL}${message.user_avatar}` : message.user_avatar;
                    return (
                      <img 
                        src={avatarUrl} 
                        alt={message.user_name} 
                        className="w-full h-full rounded-full object-cover" 
                      />
                    );
                  } else {
                    return message.user_name?.charAt(0)?.toUpperCase() || 'U';
                  }
                })()}
              </div>
              {/* Presence dot */}
              <div className="absolute -bottom-0.5 -right-0.5">
                <PresenceDot online={userOnline} size="small" />
              </div>
            </div>
            
            <span className="text-sm font-semibold text-gray-200">{message.user_name}</span>
            <span className="text-xs text-gray-500">
              {new Date(message.timestamp).toLocaleTimeString()}
              {message.edited_at && <span className="ml-1 text-gray-600">(edited)</span>}
            </span>
            
            {/* Whisper button - for all users except self */}
            {!isOwnMessage && onWhisperClick && (
              <button
                onClick={() => onWhisperClick({
                  user_id: message.user_id,
                  user_name: message.user_name,
                  user_avatar: message.user_avatar,
                  online: userOnline
                })}
                className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 rounded-lg transition-all duration-200"
                title={`Whisper ${message.user_name}`}
              >
                <WhisperIcon />
              </button>
            )}
            
            {/* Edit/Delete/React buttons */}
            {!isEditing && (
              <div className="flex space-x-2">
                {/* Reaction button for all messages */}
                <div className="relative">
                  <button
                    onClick={() => setShowReactionPicker(showReactionPicker === message.id ? null : message.id)}
                    className="p-1.5 text-gray-400 hover:text-yellow-400 hover:bg-yellow-500/20 rounded-lg transition-all duration-200"
                    title="Add reaction"
                  >
                    <Smile size={16} />
                  </button>
                  {showReactionPicker === message.id && (
                    <ReactionPicker
                      onSelect={(emoji) => addReaction(message.id, emoji)}
                      onClose={() => setShowReactionPicker(null)}
                      position="bottom"
                    />
                  )}
                </div>
                
                {/* Edit/Delete buttons - only for own messages */}
                {isOwnMessage && (
                  <>
                    <button
                      onClick={() => startEditing(message)}
                      className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-blue-500/20 rounded-lg transition-all duration-200"
                      title="Edit message"
                    >
                      <Edit3 size={16} />
                    </button>
                    <button
                      onClick={() => deleteMessage(message.id)}
                      className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded-lg transition-all duration-200"
                      title="Delete message"
                    >
                      <Trash2 size={16} />
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          
          {isEditing ? (
            // Edit mode
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={editingText}
                onChange={(e) => setEditingText(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    saveEdit();
                  } else if (e.key === 'Escape') {
                    cancelEditing();
                  }
                }}
                className="flex-1 bg-gray-600 text-white px-2 py-1 rounded text-sm"
                autoFocus
              />
              <button
                onClick={saveEdit}
                className="p-1 text-green-400 hover:text-green-300"
                title="Save changes"
              >
                <Check size={14} />
              </button>
              <button
                onClick={cancelEditing}
                className="p-1 text-gray-400 hover:text-gray-300"
                title="Cancel editing"
              >
                <XCircle size={14} />
              </button>
            </div>
          ) : (
            <>
              {/* Normal message display with emote rendering */}
              <div className={`inline-block px-3 py-2 rounded-lg text-sm ${
                isOwnMessage 
                  ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white' 
                  : 'bg-gray-700/50 text-gray-200 border border-gray-600/50'
              }`}>
                <span dangerouslySetInnerHTML={{ __html: renderEmotesInText(message.message, globalEmotes, customEmotes) }} />
              </div>
              
              {/* Reactions display */}
              <Reactions
                reactions={message.reactions || []}
                onReactionClick={(emoji) => addReaction(message.id, emoji)}
                currentUserId={user?.id}
              />
            </>
          )}
        </div>
      </div>
    );
  };

  // Get genre icon
  const getGenreIcon = (genre) => {
    const icons = {
      classics: '🎵',
      disco_funk: '🕺',
      house_tech: '🏠',
      techno: '⚡',
      trance_progressive: '⭐',
      drum_bass: '🔊',
      breakbeat_garage: '🎚️'
    };
    return icons[genre] || <Music size={16} className="text-purple-400" />;
  };

  // Effects
  useEffect(() => {
    if (isOpen && user) {
      loadRooms();
      loadEmotes(); // Load emotes when chat opens
    }
    
    return () => {
      // Cleanup polling on unmount
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isOpen, user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Heartbeat effect - send presence update every 60 seconds
  useEffect(() => {
    if (isOpen && user) {
      // Send initial heartbeat
      sendHeartbeat();
      
      // Set up interval for heartbeat
      heartbeatIntervalRef.current = setInterval(() => {
        sendHeartbeat();
      }, 60000); // 60 seconds
      
      return () => {
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
        }
      };
    }
  }, [isOpen, user, currentRoom?.id]);

  // Room presence — who's actually sitting in each room right now
  useEffect(() => {
    if (!isOpen) return;
    loadRoomPresence();
    const t = setInterval(loadRoomPresence, 30000);
    return () => clearInterval(t);
  }, [isOpen]);

  // Presence update effect - check user presence every 60 seconds
  useEffect(() => {
    if (isOpen && messages.length > 0) {
      // Fetch initial presence
      fetchPresence();
      
      // Set up interval for presence updates
      const presenceInterval = setInterval(() => {
        fetchPresence();
      }, 60000); // 60 seconds
      
      return () => {
        clearInterval(presenceInterval);
      };
    }
  }, [isOpen, messages]);

  // Handle navigation state for message highlighting
  useEffect(() => {
    if (navigationState && availableRooms.length > 0) {
      console.log('🎯 Handling navigation state:', navigationState);
      
      // Switch to the specified room if provided
      if (navigationState.room || navigationState.room_id) {
        const targetRoom = availableRooms.find(r => r.id === navigationState.room_id)
          || availableRooms.find(r => r.name === navigationState.room);
        if (targetRoom) {
          console.log('📍 Switching to room:', targetRoom.name);
          setCurrentRoom(targetRoom);
          loadMessages(targetRoom.id);
        }
      }
      
      // Set the message to highlight
      if (navigationState.highlightMessageId) {
        console.log('✨ Highlighting message:', navigationState.highlightMessageId);
        setHighlightedMessageId(navigationState.highlightMessageId);
        
        // Clear highlight after 3 seconds
        setTimeout(() => {
          setHighlightedMessageId(null);
        }, 3000);
      }
    }
  }, [navigationState, availableRooms]);

  if (!isOpen) return null;

  // Render room card (reusable for both mobile and desktop)
  const renderRoomCard = (room, compact = false) => (
    <button
      key={room.id}
      onClick={() => handleRoomSelect(room)}
      className={`w-full text-left transition-all duration-300 ${
        compact 
          ? `p-2 rounded-lg ${currentRoom?.id === room.id ? 'bg-purple-600/40' : 'bg-gray-700/30 hover:bg-gray-600/40'}`
          : `p-4 rounded-xl ${currentRoom?.id === room.id ? 'bg-gradient-to-r from-purple-600 to-blue-600 border border-purple-400/50' : 'bg-gray-700/50 border border-gray-600/50 hover:bg-gray-600/50'}`
      }`}
    >
      {compact ? (
        // Compact view for landscape sidebar
        <div className="flex items-center space-x-2">
          <span className="text-lg">{getGenreIcon(room.genre)}</span>
          <span className="text-xs text-gray-200 truncate">{room.name.split(' ')[0]}</span>
          {(roomPresence[room.id] || []).length > 0 && (
            <span className="ml-auto flex items-center gap-1 text-[9px] font-bold text-green-400">
              <span className="h-1.5 w-1.5 rounded-full bg-green-400" />{(roomPresence[room.id] || []).length}
            </span>
          )}
        </div>
      ) : (
        // Full view for portrait and desktop
        <div className="flex items-start space-x-3">
          <div className="p-2 bg-purple-600/20 rounded-lg">
            {getGenreIcon(room.genre)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm text-gray-200 mb-1">{room.name}</div>
            <div className="text-xs text-gray-400 leading-relaxed">{room.description}</div>
            <div className="flex items-center mt-2 text-xs" data-testid={`room-presence-${room.id}`}>
              <Users size={10} className={`mr-1 ${(roomPresence[room.id] || []).length ? 'text-green-400' : 'text-gray-500'}`} />
              <span className={(roomPresence[room.id] || []).length ? 'font-semibold text-green-400' : 'text-gray-500'}>
                {(roomPresence[room.id] || []).length} here now
              </span>
              <span className="ml-2 flex -space-x-1.5">
                {(roomPresence[room.id] || []).slice(0, 4).map(p => (
                  p.avatar
                    ? <img key={p.user_id} src={p.avatar} alt="" title={p.username} className="h-4 w-4 rounded-full border border-gray-900 object-cover" />
                    : <span key={p.user_id} title={p.username} className="flex h-4 w-4 items-center justify-center rounded-full border border-gray-900 bg-gradient-to-br from-cyan-500 to-fuchsia-600 text-[7px] font-bold text-white">{(p.username || '?').charAt(0).toUpperCase()}</span>
                ))}
              </span>
            </div>
          </div>
        </div>
      )}
    </button>
  );

  // Render chat area (reusable)
  const renderChatArea = () => (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      {currentRoom ? (
        <>
          {/* Chat Header */}
          <div className="p-3 sm:p-4 bg-gray-800 border-b border-gray-700/50 flex items-center flex-shrink-0">
            {/* Back button for mobile portrait */}
            {isMobile && !isLandscape && (
              <button 
                onClick={handleBackToRooms}
                className="mr-3 p-2 text-white hover:bg-gray-700 rounded-lg transition-colors"
              >
                ←
              </button>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="text-base sm:text-lg font-bold text-white truncate">{currentRoom.name}</h3>
              <p className="text-xs sm:text-sm text-gray-400 truncate">{currentRoom.description}</p>
            </div>
            {(roomPresence[currentRoom.id] || []).length > 0 && (
              <div className="mr-3 hidden items-center gap-1.5 sm:flex" data-testid="chat-header-presence" title={(roomPresence[currentRoom.id] || []).map(p => p.username).join(', ')}>
                <span className="flex -space-x-1.5">
                  {(roomPresence[currentRoom.id] || []).slice(0, 5).map(p => (
                    p.avatar
                      ? <img key={p.user_id} src={p.avatar} alt="" className="h-5 w-5 rounded-full border border-gray-800 object-cover" />
                      : <span key={p.user_id} className="flex h-5 w-5 items-center justify-center rounded-full border border-gray-800 bg-gradient-to-br from-cyan-500 to-fuchsia-600 text-[8px] font-bold text-white">{(p.username || '?').charAt(0).toUpperCase()}</span>
                  ))}
                </span>
                <span className="text-[10px] font-bold text-green-400">{(roomPresence[currentRoom.id] || []).length} here</span>
              </div>
            )}
            <button
              data-testid="chat-invite-btn"
              onClick={() => setShowInvite(true)}
              title={`Invite BeatSeekers to ${currentRoom.name}`}
              className="ml-2 flex-shrink-0 text-xs font-semibold text-cyan-400 underline decoration-dotted underline-offset-4 transition-colors hover:text-cyan-300"
            >
              + Invite to room
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-2 min-h-0">
            {messages.length > 0 ? (
              messages.map(renderMessage)
            ) : (
              <div className="text-center text-gray-400 mt-8">
                <MessageCircle size={48} className="mx-auto mb-4 text-gray-600" />
                <p>No messages yet. Start the conversation!</p>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Message Input - Always visible at bottom */}
          <div className="p-3 sm:p-4 bg-gray-800 border-t border-gray-700/50 flex-shrink-0">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowEmotePicker(true)}
                className="p-2 text-gray-400 hover:text-purple-400 hover:bg-purple-500/20 rounded-lg transition-all duration-200 flex-shrink-0"
                title="Add Emote"
              >
                <span className="text-lg sm:text-xl">😊</span>
              </button>
              
              <EmoteInput
                value={messageInput}
                onChange={setMessageInput}
                onKeyPress={handleKeyPress}
                placeholder={`Message ${currentRoom.name}...`}
                globalEmotes={globalEmotes}
                customEmotes={customEmotes}
                className="flex-1 bg-gray-700/50 border border-gray-600/50 rounded-lg px-3 py-2 text-sm sm:text-base text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent min-w-0"
              />
              <button
                onClick={sendMessage}
                disabled={!messageInput.trim()}
                className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-2 rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:from-gray-600 disabled:to-gray-600 disabled:cursor-not-allowed transition-colors flex-shrink-0"
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <MessageCircle size={48} className="mx-auto mb-4 text-gray-600" />
            <h3 className="text-lg font-bold text-white mb-2">Choose Your Vibe</h3>
            <p className="text-gray-400 text-sm">Select a genre room to start chatting</p>
          </div>
        </div>
      )}
    </div>
  );

  // MOBILE PORTRAIT: Step-based view
  if (isMobile && !isLandscape) {
    return (
      <>
      <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-start justify-center z-40 pt-1 pb-28">
        <div className="bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900 w-[96%] h-[calc(100vh-130px)] rounded-xl flex flex-col overflow-hidden border border-purple-500/30 shadow-2xl">
          
          {/* Compact Header */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-3 border-b border-purple-500/30 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-9 h-9 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                  <MessageCircle className="text-white" size={18} />
                </div>
                <div>
                  <h2 className="text-base font-bold text-white">Community Chat</h2>
                  {isConnected ? (
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-green-400 rounded-full mr-1 animate-pulse"></div>
                      <span className="text-green-300 text-xs">Connected</span>
                    </div>
                  ) : (
                    <span className="text-red-300 text-xs">{connectionError || 'Connecting...'}</span>
                  )}
                </div>
              </div>
              
              <button onClick={onClose} className="text-white/80 hover:text-white p-1.5 rounded-full transition-colors">
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Content: Either rooms list or chat */}
          {mobileView === 'rooms' ? (
            // Rooms List View
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="p-4 border-b border-gray-700/50 bg-gray-800/50">
                <h3 className="text-base font-bold text-white flex items-center">
                  <Hash className="mr-2 text-purple-400" size={18} />
                  Genre Rooms
                </h3>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {availableRooms.map((room) => renderRoomCard(room, false))}
              </div>
            </div>
          ) : (
            // Chat View
            renderChatArea()
          )}
        </div>
      </div>
      
      {/* Emote Picker Modal - Outside modal container for proper z-index */}
      <EmotePicker 
        isOpen={showEmotePicker}
        onClose={() => setShowEmotePicker(false)}
        onEmoteSelect={(emoteCode) => {
          setMessageInput(prev => prev + emoteCode);
        }}
      />
      {showInvite && currentRoom && (
        <ChatInviteModal room={currentRoom} user={user} onClose={() => setShowInvite(false)} />
      )}
      </>
    );
  }

  // MOBILE LANDSCAPE: Slim sidebar + chat
  if (isMobile && isLandscape) {
    return (
      <>
      <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-40 px-4 py-2">
        <div className="bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900 w-[92%] max-w-4xl h-[70vh] max-h-[280px] rounded-lg flex flex-col overflow-hidden border border-purple-500/30 shadow-2xl">
          
          {/* Compact Header */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-2 py-1 border-b border-purple-500/30 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-1.5">
                <MessageCircle className="text-white" size={12} />
                <h2 className="text-[11px] font-bold text-white">Community Chat</h2>
                {isConnected && (
                  <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
                )}
              </div>
              <button onClick={onClose} className="text-white/80 hover:text-white p-0.5 rounded-full transition-colors">
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Main content: Slim sidebar + Chat */}
          <div className="flex flex-1 overflow-hidden min-h-0">
            {/* Slim Sidebar */}
            <div className="w-14 bg-gray-800/80 border-r border-purple-500/20 flex flex-col overflow-y-auto py-1 px-0.5 space-y-0.5 flex-shrink-0">
              {availableRooms.map((room) => renderRoomCard(room, true))}
            </div>
            
            {/* Chat Area */}
            {renderChatArea()}
          </div>
        </div>
      </div>
      
      {/* Emote Picker Modal - Outside modal container for proper z-index */}
      <EmotePicker 
        isOpen={showEmotePicker}
        onClose={() => setShowEmotePicker(false)}
        onEmoteSelect={(emoteCode) => {
          setMessageInput(prev => prev + emoteCode);
        }}
      />
      {showInvite && currentRoom && (
        <ChatInviteModal room={currentRoom} user={user} onClose={() => setShowInvite(false)} />
      )}
      </>
    );
  }

  // DESKTOP: Original layout
  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-40 p-4">
      <div className="bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900 rounded-2xl shadow-2xl w-full max-w-5xl h-[700px] border border-purple-500/20 overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6 border-b border-purple-500/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                <MessageCircle className="text-white" size={24} />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Community Chat</h2>
                <p className="text-purple-200 text-sm">Connect with fellow Beatseekers worldwide</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {isConnected ? (
                <div className="flex items-center bg-green-500/20 px-3 py-1 rounded-full border border-green-400/30">
                  <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                  <span className="text-green-300 text-sm font-medium">Connected</span>
                </div>
              ) : (
                <div className="flex items-center bg-red-500/20 px-3 py-1 rounded-full border border-red-400/30">
                  <div className="w-2 h-2 bg-red-400 rounded-full mr-2"></div>
                  <span className="text-red-300 text-sm">{connectionError || 'Connecting...'}</span>
                </div>
              )}
              
              <button onClick={onClose} className="text-white/80 hover:text-white p-2 rounded-full transition-colors">
                <X size={24} />
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          
          {/* Sidebar - Room List */}
          <div className="w-80 bg-gray-800 border-r border-purple-500/20 flex flex-col">
            <div className="p-4 border-b border-gray-700/50">
              <h3 className="text-lg font-bold text-white mb-2 flex items-center">
                <Hash className="mr-2 text-purple-400" size={20} />
                Genre Rooms
              </h3>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {availableRooms.map((room) => renderRoomCard(room, false))}
            </div>
          </div>

          {/* Main Chat Area */}
          {renderChatArea()}
        </div>
      </div>
      
      {/* Emote Picker Modal */}
      <EmotePicker 
        isOpen={showEmotePicker}
        onClose={() => setShowEmotePicker(false)}
        onEmoteSelect={(emoteCode) => {
          setMessageInput(prev => prev + emoteCode);
        }}
      />
      {showInvite && currentRoom && (
        <ChatInviteModal room={currentRoom} user={user} onClose={() => setShowInvite(false)} />
      )}
    </div>
  );
};

export default NewCommunityChat;